import { useCallback, useEffect, useState } from "react";
import { fetchAdminAdmissions } from "../api/adminApi";
import { fetchAuditorAdmissions, type AuditorAdmissionRow } from "../api/auditorApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import type { Admission } from "../types";

function mapRow(row: AuditorAdmissionRow): Admission {
  return {
    id: String(row.id),
    date: row.date,
    time: row.time,
    patient: row.patient,
    patientSub: row.patient_sub,
    patientType: row.patient_type,
    hospital: row.hospital,
    company: row.company,
    status: row.status,
    motif: row.motif,
  };
}

export function useAuditorAdmissions(filters: {
  status?: string;
  date_start?: string;
  date_end?: string;
  hospital_id?: number;
  search?: string;
} = {}) {
  const [admissions, setAdmissions] = useState<Admission[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    const role = getStoredAccountType();
    if (!getAuthToken() || (role !== "auditor" && role !== "admin")) {
      setUsingApi(false);
      setAdmissions([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const response =
        role === "admin"
          ? await fetchAdminAdmissions(filters)
          : await fetchAuditorAdmissions(filters);
      setAdmissions(response.admissions.map(mapRow));
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les admissions.");
      setAdmissions([]);
      setTotal(0);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.date_start, filters.date_end, filters.hospital_id, filters.search]);

  useEffect(() => {
    void load();
  }, [load]);

  return { admissions, total, loading, error, usingApi, reload: load };
}
